import { DatabaseOutlined as DataManagementIcon } from '@ant-design/icons'
import type { RefObject } from 'react'
import { useEffect, useState } from 'react'
import { Link, NavLink, useNavigate } from 'react-router-dom'

import { resetDataManagementState } from '@Store/slices/dataManagementSlice'
import { LocalStorageUtility } from '@Utils/storage/StorageUtility'

import DashboardLink from './items/dashboard-link'
import ReportLink from './items/report-link'
import ReportsLink from './items/reports-link'
import SmartPriceLink from './items/smart-price-link'
import ReloaderNavlink from './reloader-navlink'
import AlertIcon from '../../assets/images/icons/alert-triangle.svg?react'
import ChevronDownIcon from '../../assets/images/icons/chevron-down.svg?react'
import ChevronUpIcon from '../../assets/images/icons/chevron-up.svg?react'
import HelpCenterIcon from '../../assets/images/icons/help-center-icon.svg?react'
import LogOutIcon from '../../assets/images/icons/log-out-icon.svg?react'
import MinimizeIcon from '../../assets/images/icons/nav-minimize-icon.svg?react'
import PrisyncLogo from '../../assets/images/icons/prisync-logo.svg?react'
import {
  BillingIcon,
  DashboardIcon,
  DynamicPricingIcon,
  ProductIcon,
  ReportsIcon,
  SettingsIcon,
  UrlDashboardIcon,
} from '../../components/icons/navigation'
import { baseApi } from '../../store/api'
import { useLogoutMutation } from '../../store/api/auth'
import { useGetExportCountQuery } from '../../store/api/product'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import { unsetTokens } from '../../store/slices/authSlice'
import { IntercomEvent } from '../../utils/helpers/getIntercomMsg'
import useIntercomInternal from '../../utils/hooks/use-intercom-internal'

export interface LogoutParams {
  refresh_token: string
  dispatch: any
  trackEventInternal: any
  logout: any
  storage: LocalStorageUtility
  navigate: any
}

export default function LargeMenu({
  toggleMenu,
  reportsRef,
  meData,
  meLoading,
}: {
  toggleMenu: () => void
  reportsRef: RefObject<HTMLButtonElement>
  meData: any
  meLoading: boolean
}): JSX.Element {
  const [logout, { isLoading }] = useLogoutMutation()

  const { currentData: exportCount } = useGetExportCountQuery()

  const { trackEventInternal } = useIntercomInternal()
  const refresh_token = useAppSelector((state) => state?.auth.refresh_token)

  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  const [openReports, setOpenReports] = useState(false)

  const onOpenReportsClick = () => {
    setOpenReports((current) => !current)
  }

  const activeLink = {
    backgroundColor: '#F5F7FF',
    color: '#2D3D85',
  }

  const handleLogout = (): void => {
    logoutClick({
      refresh_token,
      dispatch,
      trackEventInternal,
      logout,
      storage: new LocalStorageUtility(),
      navigate,
    })
  }

  const displayName = getName(meData)
  const daysLeft =
    meData && meData !== undefined ? getDaysLeft(meData?.trial_end_date) : 0

  return (
    <>
      <div className="relative z-[2] bg-white flex-none lg:w-[270px] 2xl:w-[320px] h-full hidden lg:block">
        <div className=" text-gray-700 lg:w-[270px] 2xl:w-[320px] fixed bottom-0 transition-all top-[--menu-top] border-r-2 border-solid border-gray-200 overflow-y-auto">
          <div className="flex flex-1 h-full flex-col min-h-[550px] px-4">
            <div className="flex flex-col">
              <div className="flex flex-row justify-between pt-8">
                <PrisyncLogo />
                <button onClick={toggleMenu}>
                  <MinimizeIcon />
                </button>
              </div>
              <div className="pt-8 [&>*]:px-3" style={{ color: '#344054' }}>
                <DashboardLink
                  to={'/'}
                  className="flex flex-col hover:bg-primary-50 border-0 rounded-md justify-center py-2"
                  style={({ isActive }) => (isActive ? activeLink : {})}
                >
                  {({ isActive }) => (
                    <div className=" flex flex-row items-center">
                      <DashboardIcon
                        stroke={isActive ? '#3E54B8' : '#667085'}
                      />
                      <span className="pl-4 ">Dashboard</span>
                    </div>
                  )}
                </DashboardLink>
                <ReloaderNavlink
                  to={'/products'}
                  className=" flex flex-col hover:bg-primary-50 border-0 rounded-md justify-center py-2"
                  style={({ isActive }) => (isActive ? activeLink : {})}
                >
                  {({ isActive }) => (
                    <div className="flex flex-row items-center">
                      <ProductIcon stroke={isActive ? '#3E54B8' : '#667085'} />
                      <span className="pl-4">Products</span>
                    </div>
                  )}
                </ReloaderNavlink>
                <SmartPriceLink
                  to={'/dynamic-pricing'}
                  className=" flex flex-col hover:bg-primary-50 border-0 rounded-md justify-center py-2"
                  style={({ isActive }) => (isActive ? activeLink : {})}
                >
                  {({ isActive }) => (
                    <div className="flex flex-row items-center">
                      <DynamicPricingIcon
                        stroke={isActive ? '#3E54B8' : '#667085'}
                      />
                      <span className="pl-4">Dynamic Pricing</span>
                    </div>
                  )}
                </SmartPriceLink>
                <ReloaderNavlink
                  to={'/data-management'}
                  className=" flex flex-col hover:bg-primary-50 border-0 rounded-md justify-center py-2"
                  style={({ isActive }) => (isActive ? activeLink : {})}
                >
                  {({ isActive }) => (
                    <div className="flex flex-row items-center">
                      <DataManagementIcon
                        className={`w-6 h-6 flex justify-center [&>svg]:h-6 [&>svg]:w-5 ${
                          isActive
                            ? '[&>path]:stroke-[#3E54B8]'
                            : '[&>path]:stroke-[#667085]'
                        }`}
                      />
                      <span className="pl-4">Data Management</span>
                    </div>
                  )}
                </ReloaderNavlink>
                <ReportsLink>
                  <div className="flex flex-col hover:bg-primary-50 border-0 rounded-md justify-center py-2">
                    <button
                      className="flex flex-row items-center justify-between"
                      onClick={onOpenReportsClick}
                      ref={reportsRef}
                    >
                      <div className="flex flex-row items-center w-full">
                        <div className="flex flex-row justify-between w-full">
                          <div className="flex flex-row flex-1">
                            <ReportsIcon />
                            <span className="pl-4">Reports</span>
                          </div>
                          {exportCount?.count && exportCount?.count > 0 ? (
                            <div className="rounded-full bg-primary-75 text-sm w-6 h-6 relative mr-2">
                              <span className="absolute w-6 h-6 text-xs leading-6 text-center left-0 top-0">
                                {exportCount?.count}
                              </span>
                            </div>
                          ) : null}
                        </div>
                      </div>
                      {openReports ? <ChevronUpIcon /> : <ChevronDownIcon />}
                    </button>
                  </div>
                  {openReports && (
                    <div>
                      <ReportLink
                        to={'/reports/category'}
                        className=" flex flex-col hover:bg-primary-50 border-0 rounded-md justify-center py-2 mb-1"
                        style={({ isActive }) => (isActive ? activeLink : {})}
                        reportType="Category Report"
                      >
                        <div className="flex flex-row items-center justify-between px-2">
                          <div className="flex flex-row items-center px-2">
                            <span className="pl-7">Category Reports</span>
                          </div>
                        </div>
                      </ReportLink>
                      <ReportLink
                        to={'/reports/brand'}
                        className=" flex flex-col hover:bg-primary-50 border-0 rounded-md justify-center py-2 mb-1"
                        style={({ isActive }) => (isActive ? activeLink : {})}
                        reportType="Brand Report"
                      >
                        <div className="flex flex-row items-center justify-between px-2">
                          <div className="flex flex-row items-center px-2">
                            <span className="pl-7">Brand Reports</span>
                          </div>
                        </div>
                      </ReportLink>
                      <ReloaderNavlink
                        to={'/reports/export'}
                        className=" flex flex-col hover:bg-primary-50 border-0 rounded-md justify-center py-2 mb-1"
                        style={({ isActive }) => (isActive ? activeLink : {})}
                      >
                        <div className="flex flex-row items-center justify-between px-2">
                          <div className="flex flex-row items-center justify-between px-2 w-full">
                            <span className="pl-7">Export Reports</span>
                            {exportCount?.count && exportCount?.count > 0 ? (
                              <div className="rounded-full bg-primary-75 text-sm w-6 h-6 relative">
                                <span className="absolute w-6 h-6 text-xs leading-6 text-center left-0 top-0">
                                  {exportCount?.count}
                                </span>
                              </div>
                            ) : null}
                          </div>
                        </div>
                      </ReloaderNavlink>
                    </div>
                  )}
                </ReportsLink>
                {!meLoading && meData?.is_in_trial === 1 && daysLeft > 0 && (
                  <TrialDaysReminder day={daysLeft} />
                )}
              </div>
            </div>
            <div className="flex flex-col h-full justify-end">
              <div className="py-8">
                <NavLink
                  to={'/settings'}
                  className="flex flex-col hover:bg-primary-50 border-0 rounded-md justify-center py-2"
                  style={({ isActive }) => (isActive ? activeLink : {})}
                >
                  {({ isActive }) => (
                    <div className="flex flex-row items-center pl-2">
                      <SettingsIcon stroke={isActive ? '#3E54B8' : '#667085'} />
                      <span className="pl-4">Settings</span>
                    </div>
                  )}
                </NavLink>
                <NavLink
                  to={'404-reports/'}
                  className="flex flex-col hover:bg-primary-50 border-0 rounded-md justify-center py-2"
                  style={({ isActive }) => (isActive ? activeLink : {})}
                >
                  {({ isActive }) => (
                    <div className="flex flex-row items-center pl-2">
                      <UrlDashboardIcon
                        stroke={isActive ? '#3E54B8' : '#667085'}
                      />
                      <span className="pl-4">URL Dashboard</span>
                    </div>
                  )}
                </NavLink>
                <NavLink
                  className="flex flex-col hover:bg-primary-50 border-0 rounded-md justify-center py-2"
                  to={'/billing'}
                  style={({ isActive }) => (isActive ? activeLink : {})}
                >
                  {({ isActive }) => (
                    <div className="flex flex-row items-center pl-2">
                      <BillingIcon stroke={isActive ? '#3E54B8' : '#667085'} />
                      <span className="pl-4">Billing</span>
                    </div>
                  )}
                </NavLink>
                <div className="flex flex-col hover:bg-primary-50 border-0 rounded-md justify-center py-2">
                  <a
                    href="https://helpcenter.prisync.com/hc/en-us"
                    target="_blank"
                    rel="noreferrer noopener"
                    className="flex flex-row items-center justify-between pl-2"
                  >
                    <div className="flex flex-row items-center">
                      <HelpCenterIcon />
                      <span className="pl-4">Help Center</span>
                    </div>
                  </a>
                </div>
              </div>
            </div>

            <LogoutButton onClick={handleLogout} name={displayName} />
          </div>
        </div>
      </div>
    </>
  )
}

export function LogoutButton({
  onClick,
  name,
}: {
  onClick: () => void
  name: string
}): JSX.Element {
  return (
    <div className="flex flex-col border-t-2 border-gray-200">
      <div className="py-5">
        <button
          className="flex flex-col justify-center py-2 "
          onClick={onClick}
        >
          <div className="flex flex-row items-center pl-2">
            <LogOutIcon />
            <span className="pl-4">Log out</span>
          </div>
        </button>
        <div className="flex flex-col items-start pl-2 pr-5">
          <span className="text-medium font-normal w-full text-gray-500 whitespace-nowrap overflow-hidden text-ellipsis">
            {name}
          </span>
        </div>
      </div>
    </div>
  )
}

export function TrialDaysReminder({ day }: { day: number }): JSX.Element {
  return (
    <>
      <div className="flex sm:pl-4 container mt-4 py-4 border border-solid border-warning-300 bg-warning-25 rounded-lg">
        <div className="flex pt-1 ">
          <AlertIcon />
        </div>
        <div className="flex flex-col pl-2 w-full">
          <span className="text-warning-700 mb-1 text-medium">
            Trial Ending
          </span>
          <span className="font-normal text-xs text-warning-600">
            Your free trial period ends{' '}
            {day > 1 ? 'in ' + day + ' days' : 'today'}.
          </span>
          <Link
            to="/billing/plans"
            className="font-normal text-xs text-warning-600 underline"
          >
            Explore Plans
          </Link>
        </div>
      </div>
    </>
  )
}

export const logoutClick = async ({
  refresh_token,
  dispatch,
  trackEventInternal,
  logout,
  storage,
  navigate,
}: LogoutParams): Promise<void> => {
  try {
    await logout({ refresh_token }).unwrap()
  } finally {
    storage.remove('urlFastFetchDoNotShowAgain')
    dispatch(unsetTokens())
    dispatch(baseApi.util.resetApiState())
    dispatch(resetDataManagementState())
    trackEventInternal(IntercomEvent['logout-success'])
    setTimeout(() => {
      if (CURRENT_ENV === 'dev' || CURRENT_ENV === 'test') {
        navigate('/login')
      } else {
        window.location.replace(`${WORDPRESS_HOST}/login`)
      }
    }, 500)
  }
}

export const getName = (meData: any): string => meData?.name

export const getDaysLeft = (trialEndDate: string): number => {
  const endDate = new Date(trialEndDate)
  const timeDifference = endDate.getTime() - new Date().getTime()

  return Math.ceil(timeDifference / (1000 * 60 * 60 * 24))
}
